import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import _ from "lodash";

import { ACTION_BY_FILTERS_KEY } from "@/Constants/common";
import { _fetchTerminatedTimesheetList } from "@/services/timesheet";

import { formatTimesheets } from "../../utilities/dataFormatter";
const initialState = {
  isLoading: false,
  isPaginationLoading: false,
  terminatedTimesheets: [],
  selectedCandidate: [], // list of candidate id
  selectedSignOffIds: [], // list of signoff ids
  selectedTimesheets: [],
  filters: {
    search: "",
    sessionType: [],
    candidateReferenceNo: "",
    contractReferenceNo: "",
    candidateId: [],
    candidateRole: null,
    complianceStatus: null,
    miles: null,
    candidateLicenseNo: "",
    candidatePostcode: "",
    workedDateFrom: "",
    workedDateTo: "",
    contractDateFrom: "",
    contractDateTo: "",
    terminatedBy: [],
    terminatedReason: [],
    ...ACTION_BY_FILTERS_KEY,
  },
  sorting: {
    sortByColumn: "",
    sortByOrder: "",
  },
  totalCount: 0,
  isSelectedAll: false,
  oldFilters: {},
  pagination: {
    currentPage: 1,
    totalPage: 1,
    isLastPage: false,
  },
  showFilters: false,
  toggleSelected: false,
  viewMoreInfo: {},
  showTimesheetMoreInfoModal: false,
  showSignOffModal: false,
  selectedSignOffData: {},
};

export const fetchTerminatedTimesheets = createAsyncThunk(
  "timesheetsTerminated/fetchTerminatedTimesheets",
  async (data, { dispatch, getState }) => {
    const {
      clientTimesheetTerminated: {
        filters,
        pagination,
        sorting,
        terminatedTimesheets,
      },
    } = getState();
    if (_.isEmpty(data)) {
      data = {
        ...filters,
      };
    }
    if (pagination.isLastPage) {
      return dispatch(setTerminatedTimesheetsLoading(false));
    }

    dispatch(setPaginationLoading(pagination.currentPage !== 1));
    dispatch(toggleSelectedAll(false));

    const _data = {
      ...data,
      currentPage: pagination.currentPage,
      ...sorting,
    };
    let response = _fetchTerminatedTimesheetList(_data);
    response
      .then((res) => {
        const {
          data: { timesheet_list, meta },
        } = res;
        const { currentPage, lastPage, isLastPage, total: totalCount } = meta;
        let data = formatTimesheets(timesheet_list, 1);
        dispatch(
          updatePagination({
            currentPage: currentPage + 1,
            totalPage: lastPage,
            isLastPage: isLastPage,
          }),
        );
        let prevList = currentPage === 1 ? [] : _.uniq(terminatedTimesheets);

        let firstTimesheetId = data.length > 0 ? data[0].timesheet_id : null;
        dispatch(updateTotalCount(totalCount));

        if (
          prevList.length > 0 &&
          prevList.find((item) => item.timesheet_id === firstTimesheetId) !==
            undefined
        ) {
          //
        } else {
          dispatch(updateTerminatedTimesheets([...prevList, ...data]));
        }
        return res;
      })
      .catch((error) => {
        // handleAPICatch(error);
      })
      .finally(() => {
        setTimeout(() => {
          dispatch(setTerminatedTimesheetsLoading(false));
          dispatch(setPaginationLoading(false));
        }, 10);
      });
    return response;
  },
);

const timesheetTerminated = createSlice({
  name: "timesheetsTerminated",
  initialState,
  reducers: {
    updateTerminatedTimesheets: (state, action) => {
      state.terminatedTimesheets = action.payload;
    },
    setTerminatedTimesheetsLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setPaginationLoading(state, action) {
      state.isPaginationLoading = action.payload; // New reducer for pagination loading
    },
    updateSelectedCandidate: (state, action) => {
      state.selectedCandidate = action.payload;
      if (action.payload.length === 0) {
        state.isSelectedAll = false;
      }
    },
    updateSelectedTimesheets: (state, action) => {
      state.selectedTimesheets = action.payload;
      if (action.payload.length === 0) {
        state.isSelectedAll = false;
      }
    },
    updateSelectedSignOffIds: (state, action) => {
      state.selectedSignOffIds = action.payload;
    },
    deSelectAllCandidate: (state) => {
      state.selectedCandidate = [];
      state.selectedSignOffIds = [];
      state.isSelectedAll = false;
    },
    updateFilters: (state, action) => {
      if (_.isEmpty(action.payload)) {
        state.filters = {
          search: state.filters.search,
          ...initialState.filters,
        };
        return;
      } else {
        if (action.payload.reset) {
          state.filters = initialState.filters;
        } else {
          state.filters = { ...state.filters, ...action.payload };
        }
      }
    },
    updateSorting: (state, action) => {
      state.sorting = action.payload;
    },
    updatePagination: (state, action) => {
      state.pagination = {
        ...state.pagination,
        ...action.payload,
      };
    },
    showFilterModal: (state, action) => {
      if (action.payload) {
        state.oldFilters = state.filters;
      }
      state.showFilters = action.payload;
    },
    updateToggleSelected: (state, action) => {
      state.toggleSelected = !state.toggleSelected;
    },
    reStateFilters: (state, action) => {
      state.filters = state.oldFilters;
    },
    toggleTimesheetMoreInfoModal: (state, action) => {
      state.showTimesheetMoreInfoModal = action.payload;
    },
    updateViewMoreInfo: (state, action) => {
      state.viewMoreInfo = action.payload;
    },
    updateTotalCount: (state, action) => {
      state.totalCount = action.payload;
    },
    toggleSelectedAll: (state, action) => {
      state.isSelectedAll = action.payload;
    },
    toggleSignOffModal: (state, action) => {
      if (action.payload === undefined) {
        state.showSignOffModal = !state.showSignOffModal;
      } else {
        state.showSignOffModal = action.payload;
      }
    },
    toggleSignOffModalData: (state, action) => {
      state.selectedSignOffData = action.payload;
    },
  },
});
export const {
  updateTerminatedTimesheets,
  setTerminatedTimesheetsLoading,
  setPaginationLoading,
  updateSelectedCandidate,
  updateSelectedSignOffIds,
  updateSelectedTimesheets,
  deSelectAllCandidate,
  updateFilters,
  updateSorting,
  updatePagination,
  showFilterModal,
  updateToggleSelected,
  reStateFilters,
  toggleTimesheetMoreInfoModal,
  updateViewMoreInfo,
  updateTotalCount,
  toggleSelectedAll,
  toggleSignOffModal,
  toggleSignOffModalData,
} = timesheetTerminated.actions;

export default timesheetTerminated.reducer;
